import { useRef, useEffect } from 'react';

import { toast } from 'src/components/snackbar';

import { useSubscription } from './useSubscription';
import { isPackEnabledFeature } from '../types/billing';
import { useNotificationDismissal } from '../context/NotificationDismissalContext';

// ----------------------------------------------------------------------
// Single-mount hook that fires the "Usage Warning" toast for non-pack
// features (candidates, job_posts, etc.). Must be mounted exactly once —
// see DashboardLayout. Previously this lived inside usePlanAccess, but
// that hook is consumed by 15+ components and 5+ of them mount on the
// same page (candidates view). Each consumer ran its own effect, and
// because React state updates from sibling effects in the same commit
// aren't visible until the next render, every consumer saw an empty
// dismissal set and fired the toast — surfacing 3–4 duplicate banners.
//
// Pack-enabled features (interviews, screenings) intentionally skip this
// path — they're surfaced via InterviewLimitBanner instead.
// ----------------------------------------------------------------------

function planWarningDismissKey(orgId: string | null, feature: string): string {
  return `plan-warning:${orgId ?? 'no-org'}:${feature}`;
}

export function usePlanWarningToasts(): void {
  const { warnings, orgId } = useSubscription();
  const { isDismissed, dismiss, reset: resetDismissals } = useNotificationDismissal();
  const lastWarningOrgIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!warnings || warnings.length === 0) return;

    if (lastWarningOrgIdRef.current !== orgId) {
      resetDismissals();
      lastWarningOrgIdRef.current = orgId;
    }

    let count = 0;
    for (const warning of warnings) {
      if (count >= 2) break;
      if (isPackEnabledFeature(warning.feature)) continue;

      const key = planWarningDismissKey(orgId, warning.feature);
      if (isDismissed(key)) continue;

      dismiss(key);
      toast.warning('Usage Warning', { description: warning.message, duration: 5000 });
      count += 1;
    }
  }, [warnings, orgId, isDismissed, dismiss, resetDismissals]);
}
